import dayjs from 'dayjs';
import esLocale from 'dayjs/locale/es';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.locale(esLocale);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(advancedFormat);

/**
 * Instancia de Dayjs con plugins y localización en español.
 */
export const dayjsInstance = dayjs;

/**
 * Propiedades para el formato de fecha.
 */
type FormatProps = {
  includeTime?: boolean;
  shortDate?: boolean;
};

/**
 * Formatea una cadena de fecha.
 *
 * @param dateString - La cadena de fecha a formatear.
 *
 * @param formatProps - Opciones de formato.
 *
 * @returns La fecha formateada.
 */
export const formatDate = (dateString: string, formatProps?: FormatProps) => {
  const date = new Date(dateString);
  const { includeTime } = formatProps ?? { includeTime: false, shortDate: false };

  if (isNaN(date.getTime())) {
    return 'Fecha no disponible';
  }

  const utcDate = new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds()
    )
  );

  const dateFormatted = new Intl.DateTimeFormat('es-ES', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  })
    .format(utcDate)
    .replace(/^(\w+)/, (m) => m.charAt(0).toUpperCase() + m.slice(1).toLowerCase())
    .replace(/^(\w+)\s+(\d+)/, '$1, $2')
    .replace(/(\d{4})/, ', $1')
    .replace(/(\d+)\s+([a-zA-Z]+)\s*,\s*(\d{4})/, '$1 de $2, $3')
    .replace(/\s+,/g, ',')
    .replace(/de ([a-z]+),/, (_, month) => `de ${month.charAt(0).toUpperCase() + month.slice(1)},`);

  if (!includeTime) return dateFormatted;

  const hours = dateString.includes('Z') ? date.getUTCHours() : date.getHours();
  const minutes = dateString.includes('Z') ? date.getUTCMinutes() : date.getMinutes();

  const period = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12;
  const timeFormatted = `${formattedHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;

  return `${dateFormatted}, ${timeFormatted}`;
};

/**
 * Formatea una fecha con un patrón específico.
 *
 * @param date - La fecha a formatear.
 *
 * @param format - El patrón de formato.
 *
 * @returns La fecha formateada.
 */
export const formatDateWithPattern = (
  date: string | number | Date,
  format = 'DD-MM-YYYY HH:mm:ss'
): string => {
  const dateObj = new Date(date);

  if (isNaN(dateObj.getTime())) {
    return 'Fecha no disponible';
  }

  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  const hours = dateObj.getHours().toString().padStart(2, '0');
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');
  const seconds = dateObj.getSeconds().toString().padStart(2, '0');

  return format
    .replace('DD', day)
    .replace('MM', month)
    .replace('YYYY', year.toString())
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds);
};

/**
 * Formatea la fecha de ayer con la hora actual.
 *
 * @returns La fecha de ayer y la hora actual formateadas.
 */
export const formatYesterdayWithCurrentTime = (): string => {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate());

  const dateStr = formatDate(yesterday.toISOString());

  const hours = now.getHours();
  const minutes = now.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12;
  const timeStr = `${formattedHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;

  return `${dateStr} - ${timeStr}`;
};

/**
 * Formatea la fecha de entrega.
 *
 * @param dateString - La cadena de fecha a formatear.
 *
 * @returns La fecha de entrega formateada.
 */
export const formatDeliveryDate = (dateString: string | undefined): string => {
  if (!dateString) return '-';

  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return 'Fecha no disponible';
  }

  const formattedDate = date.toLocaleDateString('es-PA', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

  const capitalizedDate = formattedDate.replace(/^\w/, (c) => c.toUpperCase());

  return capitalizedDate
    .replace(/(\d+)\s+([A-Za-z]+)\s+(\d{4})/, '$1 de $2 de $3')
    .replace(
      /de ([a-z]+) de/,
      (_, month) => `de ${month.charAt(0).toUpperCase() + month.slice(1)}, `
    );
};

/**
 * Formatea una fecha en formato corto.
 *
 * @param dateString - La cadena de fecha a formatear.
 *
 * @returns La fecha formateada en formato corto.
 */
export const formatShortDate = (dateString: string | undefined): string => {
  if (!dateString) return 'Fecha no disponible';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return 'Fecha no disponible';
  }

  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC'
  };

  let formattedDate = new Intl.DateTimeFormat('es-ES', options)
    .format(date)
    .replace(/^(\w+)/, (m) => m.charAt(0).toUpperCase() + m.slice(1).toLowerCase())
    .replace(/(\d+)\s+([a-zA-Z]+)\s+(\d{4})/, '$1 de $2, $3');
  formattedDate = formattedDate.replace(/\s+a\.\s*m\.$/i, ' AM').replace(/\s+p\.\s*m\.$/i, ' PM');

  return formattedDate;
};

/**
 * Formatea una fecha en formato corto con guiones.
 *
 * @param dateString - La cadena de fecha a formatear.
 *
 * @returns La fecha formateada en formato corto con guiones.
 */
export const formatShortDateWithDash = (dateString: string | undefined): string => {
  if (!dateString) return 'Fecha no disponible';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return 'Fecha no disponible';
  }

  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC'
  };

  let formattedDate = new Intl.DateTimeFormat('es-ES', options)
    .format(date)
    .replace(/^(\w+)/, (m) => m.charAt(0).toUpperCase() + m.slice(1).toLowerCase())
    .replace(/(\d+)\s+([a-zA-Z]+)\s+(\d{4})/, '$1 de $2, $3');
  formattedDate = formattedDate.replace(/\s+a\.\s*m\.$/i, ' AM').replace(/\s+p\.\s*m\.$/i, ' PM');

  formattedDate = formattedDate.replace(/(\d{4}),\s+/, '$1 - ');

  return formattedDate;
};

export const formatPlainDate = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  const d = dayjsInstance(dateString);
  if (d.isValid()) {
    let s = d.utc().format('ddd D [de] MMMM YYYY');
    s = s.replace(/^([A-Za-zÁÉÍÓÚáéíóú]{3})\./u, (_, abbr) => abbr); // quitar punto del abreviado
    return s.replace(/^(.)(.*)$/u, (_, f, r) => f.toUpperCase() + r);
  }
  const cleaned = dateString
    .replace(/,\s*\d{1,2}:\d{2}\s*(AM|PM)$/i, '')
    .replace(/,\s*\d{1,2}:\d{2}$/i, '')
    .replace(/\s+-\s+\d{1,2}:\d{2}\s*(AM|PM)$/i, '')
    .replace(/,/g, '')
    .trim();
  const m = cleaned.match(
    /^([A-Za-zÁÉÍÓÚáéíóú]{3,})\s+(\d{1,2})\s+de\s+([A-Za-zÁÉÍÓÚáéíóú]+)\s+(\d{4})$/u
  );
  if (m) {
    const dayName = m[1];
    const day = m[2];
    const month = m[3];
    const year = m[4];
    const cap = dayName.replace(/^(.)(.*)$/u, (_, f, r) => f.toUpperCase() + r);
    return `${cap} ${day} de ${month} ${year}`;
  }
  const fallback = new Date(dateString);
  if (!isNaN(fallback.getTime())) {
    const s = new Intl.DateTimeFormat('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC'
    }).format(fallback);
    return s.replace(/^(.)(.*)$/u, (_, f, r) => f.toUpperCase() + r).replace(/,/g, '');
  }
  return '-';
};
